import React, { useEffect, useState } from "react"

import SSOLoaderCard from "components/common/ssoLoaderCard"
import Cookies from "js-cookie"
import { useTranslation } from "react-i18next"
import { UserRole } from "ts/enums/User"
import { IAuthData } from "ts/interfaces/Auth"
import { getAuthCookieDomain } from "utils/domain"

interface ICleverResponseData {
  token: string
  finalization_required: boolean
  first_name?: string
  last_name?: string
  is_email_confirmed?: boolean
}

interface ICleverSSOLoaderProps {
  isLoading: boolean
  userType: "teacher" | "student"
  onReset?: () => void
  onAuthSuccess?: (authData: IAuthData) => void
}

const CleverSSOLoader: React.FC<ICleverSSOLoaderProps> = ({
  isLoading,
  userType,
  onReset,
  onAuthSuccess
}) => {
  const { t } = useTranslation()
  const [showError, setShowError] = useState(false)
  const [errorType, setErrorType] = useState<string | null>(null)
  const [parsingError, setParsingError] = useState(false)

  // Also serves as the error display for TeachHub/ClassLink SSO's email_missing /
  // authentication_failed codes (see useOpenIdSSO.ts) — shared rather
  // than duplicated since the messages are the same for both providers.
  const getErrorMessage = (error: string) => {
    switch (error) {
      case "email_missing":
        return t("noAuthFlow.ssoEmailMissing")
      case "wrong_user_type":
        return t("noAuthFlow.ssoWrongUserType")
      case "authentication_failed":
        return t("noAuthFlow.ssoAuthenticationFailed")
      default:
        return t("noAuthFlow.ssoMissingAuthData")
    }
  }

  const checkForErrors = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const error = urlParams.get("error")
    
    if (error) {
      setErrorType(error)
      setShowError(true)
      return true
    }
    return false
  }

  const parseCookiesData = (): ICleverResponseData | null => {
    const authData = Cookies.get("auth_data")
    
    if (!authData) {
      return null
    }
    
    try {
      // decoding string with escaped characters
      const decodedString = authData
        .replace(/\\"/g, "\"")  // \" -> "
        .replace(/\\054/g, ",") // \054 -> , (ASCII code for comma)
      
      const parsedData = JSON.parse(decodedString)
      return parsedData
    } catch {
      return null
    }
  }

  const authorizeUser = (parsedData: ICleverResponseData) => {
    try {
      const authData: IAuthData = {
        token: parsedData.token,
        role: userType === "teacher" ? UserRole.TEACHER : UserRole.STUDENT,
        finalized: !parsedData.finalization_required,
        is_email_confirmed: parsedData.is_email_confirmed,
        isCleverLogin: true,
        first_name: parsedData.first_name,
        last_name: parsedData.last_name,
      }
      
      if (onAuthSuccess) {
        onAuthSuccess(authData)
      }
    } catch {
      setParsingError(true)
      setShowError(true)
    }
  }

  useEffect(() => {
    if (isLoading) {
      // Check for URL error params first
      if (checkForErrors()) {
        return
      }
      
      setShowError(false)
      setErrorType(null)
      setParsingError(false)

      // Check if data is already available in cookies (user returned from Clever)
      // Add small delay to allow cookies to be set
      const checkDataTimer = setTimeout(() => {
        const parsedData = parseCookiesData()
        if (parsedData && !!parsedData.token) {
          authorizeUser(parsedData)
          return
        }
      }, 500) 
      
      const errorTimer = setTimeout(() => {
        const parsedData = parseCookiesData()
        if (!parsedData || !parsedData.token) {
          setShowError(true)
        }
      }, 10000)

      return () => {
        clearTimeout(checkDataTimer)
        clearTimeout(errorTimer)
      }
    }
  }, [isLoading])

  if (!isLoading) {
    return null
  }

  const handleReset = () => {
    setShowError(false)
    setErrorType(null)
    setParsingError(false)
    Cookies.remove("auth_data", { domain: getAuthCookieDomain() })
    onReset?.()
  }

  return (
    <SSOLoaderCard
      showError={showError}
      loadingText={t("noAuthFlow.cleverSSOLoading")}
      errorText={
        errorType
          ? getErrorMessage(errorType)
          : parsingError
          ? t("noAuthFlow.ssoParsingError")
          : t("noAuthFlow.ssoMissingAuthData")
      }
      onReset={handleReset}
    />
  )
}

export default CleverSSOLoader 